/**
 * The dock's scope chip and its minimised working strip (TASK-766, TASK-789).
 *
 * Extracted from `Dock.tsx` by TASK-794, which found `DockInner` at 637 lines —
 * one function owning scope resolution, transport, the chunk-handler map, the
 * send path and all of the chrome. These two pieces are pure presentation over
 * props, so they come out first and cleanest.
 *
 * They live together because they are one affordance in two states: with the
 * sheet open the chip is a plain disclosure; with it shut the chip carries the
 * count or the live dot and the strip says what is happening and how to get
 * back. The rules that keep them from competing (badge and dot are mutually
 * exclusive; neither shows while the sheet is open) are only checkable if both
 * are in view.
 */

import React from 'react';
import { ChevronDown, ChevronUp, MessagesSquare, X } from 'lucide-react';

/**
 * The pulsing "a turn is running" dot.
 *
 * TASK-790: one definition, because the class string carries TASK-766's
 * `prefers-reduced-motion` guarantee and it was written out twice — the chip and
 * the working strip — where the two copies could drift apart silently.
 *
 * TASK-789: `aria-hidden`, deliberately. It previously carried
 * `aria-label="Working"` on a role-less <span>, which is not reliably exposed
 * and, inside the chip button, concatenated into that button's accessible name
 * differently across engines. The working state is announced once, by the
 * `role="status"` strip below; the dot is its visual echo.
 */
export const LiveDot: React.FC<{ testId?: string }> = ({ testId }) => (
  <span
    data-testid={testId}
    aria-hidden="true"
    className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary motion-reduce:animate-none"
  />
);

export interface WorkingStripProps {
  /** Shown only while the sheet is shut and a turn is live. */
  visible: boolean;
  /** What is happening — a running tool's label, or the phase. */
  label: string;
  onShowConversation: () => void;
}

/**
 * TASK-789: the region is mounted ALWAYS, and only its content changes. A live
 * region that appears with its text already inside it is not announced by most
 * screen readers — they report changes within an existing region — so the one
 * affordance that says "this is happening, and here is the way back" was the one
 * a screen-reader user never heard. Idle it is `sr-only` and empty, which costs
 * no layout.
 */
export const WorkingStrip: React.FC<WorkingStripProps> = ({
  visible,
  label,
  onShowConversation,
}) => (
  <div
    data-testid="dock-working-strip"
    role="status"
    aria-live="polite"
    className={
      visible
        ? 'mx-auto mb-1.5 flex max-w-3xl items-center gap-2 text-xs text-muted-foreground'
        : 'sr-only'
    }
  >
    {visible && (
      <>
        <LiveDot />
        <span className="truncate">{label}</span>
        <button
          type="button"
          onClick={onShowConversation}
          className="shrink-0 underline decoration-dotted transition-colors hover:text-foreground"
        >
          Show conversation
        </button>
      </>
    )}
  </div>
);

export interface ScopeChipProps {
  label: string;
  sheetOpen: boolean;
  onToggle: () => void;
  /** Live while a turn runs; mutually exclusive with the count. */
  live: boolean;
  messageCount: number;
  /** The id of the sheet this discloses — only while it is mounted. */
  controls: string;
  /** TASK-788: present only for a task thread, which is the only leavable scope. */
  onLeaveThread?: () => void;
}

/**
 * TASK-766: this is a disclosure, not a label. It read as one only through a
 * `title` tooltip — invisible on touch, and to anyone not hovering a scope name
 * for no reason — so a collapsed transcript had no discoverable way back. Icon,
 * chevron and ARIA say what it is; the count and the live dot say there is
 * something behind it.
 */
export const ScopeChip: React.FC<ScopeChipProps> = ({
  label,
  sheetOpen,
  onToggle,
  live,
  messageCount,
  controls,
  onLeaveThread,
}) => (
  <>
    <button
      type="button"
      onClick={onToggle}
      className="mb-0.5 flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      data-testid="dock-scope-chip"
      aria-expanded={sheetOpen}
      // TASK-789: only while the sheet is mounted. `TranscriptSheet` returns
      // null when closed, so an unconditional `aria-controls` referenced a
      // missing id at exactly the moment the disclosure matters most —
      // tolerated by screen readers, flagged by axe.
      aria-controls={sheetOpen ? controls : undefined}
      title={sheetOpen ? 'Collapse conversation' : 'Open conversation'}
    >
      <MessagesSquare className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      <span className="max-w-[180px] truncate">{label}</span>
      {/* Badge and dot only exist while the sheet is shut — with it open the
          transcript itself is the state, and the chip stays quiet. */}
      {!sheetOpen && live && <LiveDot testId="dock-chip-live" />}
      {!sheetOpen && !live && messageCount > 0 && (
        <span
          data-testid="dock-chip-count"
          className="shrink-0 rounded-full bg-muted px-1.5 text-[10px] font-medium text-foreground"
        >
          {messageCount}
        </span>
      )}
      {sheetOpen ? (
        <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
    </button>
    {/* TASK-788: the way out of a task thread. TASK-769 opened threads on
        delegation and gave the advisor no way to leave one — every route that
        did not name a different client kept showing it until a reload, so the
        general assistant was unreachable and `closeTaskThread` had no caller at
        all. A sibling button, not a nested one: buttons do not nest. */}
    {onLeaveThread && (
      <button
        type="button"
        onClick={onLeaveThread}
        className="mb-0.5 shrink-0 rounded-full border border-border p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        data-testid="dock-thread-dismiss"
        title="Leave this task thread"
      >
        <X className="h-3 w-3" aria-hidden="true" />
        <span className="sr-only">Leave this task thread</span>
      </button>
    )}
  </>
);
